/**
 * Pure helpers that flatten the assorted redux/service shapes (Immutable
 * Records, Maps, Lists and plain objects) into the simple view-model the Credit
 * Checker presentational components expect. No React, no side effects.
 */
import moment from "moment";
import {
    CONSTITUTION_TYPES,
    RESIDENCE_TYPES,
    BUSINESS_PRODUCT_TYPE,
    CASE_CATEGORY_TYPES
} from "constants/enums";
import {
    KYC_DOCUMENT_TYPES,
    BUREAU_DOCUMENT_TYPES,
    COMPANY_ADDRESS_TYPES,
    COMPANY_DOCUMENT_TILES,
    COMPANY_PAN_TILE,
    PARTNERSHIP_DEED_TILE,
    PARTNERS_LIST_TILE,
    TRIANGULATION_DOCUMENT_TILES,
    RECOMMENDATION_DOCUMENT_MATCH
} from "./constants";

// Null, undefined and empty string all count as "the API sent nothing".
// 0 and false are real values and are NOT blank.
export function isBlank(value) {
    return value === null || value === undefined || value === "";
}

// Reads a key from an Immutable Record/Map or a plain object.
function read(obj, key) {
    if (obj === null || obj === undefined) {
        return undefined;
    }
    if (typeof obj.get === "function") {
        return obj.get(key);
    }
    return obj[key];
}

// Reads a value that may live at the top level or under `case_level_data`.
function readCaseLevel(record, key) {
    const top = read(record, key);
    if (top !== undefined && top !== null && top !== "") {
        return top;
    }
    return read(read(record, "case_level_data"), key);
}

// Converts an Immutable List OR a keyed Map into an array of its VALUES.
// company.addresses and company.associates are stored as Maps keyed by id
// (see data-pipelines.js), so a plain `.toArray()` on them does not yield the
// records — `valueSeq()` does, and works for Lists too.
function toArray(list) {
    if (!list) {
        return [];
    }
    if (typeof list.valueSeq === "function") {
        return list.valueSeq().toArray();
    }
    if (typeof list.toArray === "function") {
        return list.toArray();
    }
    return Array.isArray(list) ? list : [];
}

function firstNonEmpty(record, keys) {
    for (let i = 0; i < keys.length; i += 1) {
        const value = read(record, keys[i]);
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }
    return undefined;
}

// Buckets a bureau score into a qualitative band + colour + range label.
// Below-300 (typically -1 = "New To Credit") is amber, not red — it's not a
// bad score, just not enough history to score against.
export function scoreBand(score) {
    const value = Number(score);
    if (!value || Number.isNaN(value)) {
        return { label: "No score", tone: "muted", range: "" };
    }
    if (value >= 750) {
        return { label: "Excellent", tone: "good", range: "750-900" };
    }
    if (value >= 700) {
        return { label: "Good", tone: "good", range: "700-749" };
    }
    if (value >= 650) {
        return { label: "Fair", tone: "warn", range: "650-699" };
    }
    if (value < 300) {
        return { label: "New to Credit", tone: "warn", range: "< 300" };
    }
    return { label: "Low", tone: "bad", range: "300-649" };
}

// Normalises a document's `file` (string path, Immutable Map or object) into a
// bare path string.
function normaliseFilePath(file) {
    if (!file) {
        return null;
    }
    if (typeof file === "string") {
        return file;
    }
    return read(file, "file") || null;
}

// Coerces any "url-ish" value (string, or object with file/file_url/url) into a
// bare string path, or null. Bureau report payloads vary by endpoint version.
function asPath(value) {
    if (!value) {
        return null;
    }
    if (typeof value === "string") {
        return value;
    }
    return (
        read(value, "file") ||
        read(value, "file_url") ||
        read(value, "url") ||
        null
    );
}

// Flattens the redux `documents` slice into a plain list of document descriptors.
// The `document` field arrives either as a bare numeric type id (case documents)
// or as a nested object carrying { id, document_name } (company documents). We
// normalise to a numeric `document` id plus a `documentName` string so callers
// can match on either.
function documentRecordsToArray(documents) {
    const data = read(documents, "data");
    const records = toArray(
        data && typeof data.toList === "function" ? data.toList() : data
    );
    return records
        .map(record => {
            const doc = read(record, "data") || record;
            const rawDoc = read(doc, "document");
            const isObj = rawDoc && typeof rawDoc === "object";
            const documentId = isObj ? read(rawDoc, "id") : rawDoc;
            const documentName = isObj
                ? read(rawDoc, "document_name")
                : read(doc, "doc_name") || read(doc, "document_type");
            return {
                document: documentId,
                documentName: documentName
                    ? String(documentName).toLowerCase()
                    : null,
                associate: read(doc, "associate"),
                company: read(doc, "company"),
                file: normaliseFilePath(read(doc, "file"))
            };
        })
        .filter(doc => doc.document !== null && doc.document !== undefined);
}

// Matches a company-level document (associate not set) by candidate type ids OR
// case-insensitive name substring. Matching on both guards against unknown ids.
function findCompanyDoc(docList, matcher) {
    if (!matcher) {
        return null;
    }
    const ids = (matcher.ids || []).map(Number);
    const names = matcher.names || [];
    const doc = docList.find(item => {
        if (!item.file) {
            return false;
        }
        // Company docs are not tied to an applicant.
        if (item.associate !== null && item.associate !== undefined) {
            return false;
        }
        const byId = ids.includes(Number(item.document));
        const byName =
            item.documentName &&
            names.some(name => item.documentName.indexOf(name) !== -1);
        return byId || byName;
    });
    return doc ? { file: doc.file } : null;
}

function findDoc(docList, associateId, typeIds) {
    const doc = docList.find(
        item =>
            Number(item.associate) === Number(associateId) &&
            typeIds.includes(Number(item.document)) &&
            item.file
    );
    return doc ? { file: doc.file } : null;
}

// Like findCompanyDoc but without the associate restriction — for source
// documents (GST / ledger / bank statement) that match purely by type/name.
function findAnyDoc(docList, matcher) {
    if (!matcher) {
        return null;
    }
    const ids = (matcher.ids || []).map(Number);
    const names = matcher.names || [];
    const doc = docList.find(item => {
        if (!item.file) {
            return false;
        }
        const byId = ids.includes(Number(item.document));
        const byName =
            item.documentName &&
            names.some(name => item.documentName.indexOf(name) !== -1);
        return byId || byName;
    });
    return doc ? { file: doc.file } : null;
}

// Shareholding as a display %: "100.00" -> "100%", "33.33" -> "33.33%".
// Trailing zeros are dropped so whole numbers read cleanly. 0 is a real value
// (a co-applicant with no stake), so only null/undefined/"" render as absent —
// mirrors the CompanyRecord's own `shareholding >= 0 ? … : "--"` rule.
function formatShareholding(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const n = Number(value);
    if (!isFinite(n) || Number.isNaN(n)) {
        return null;
    }
    return `${parseFloat(n.toFixed(2))}%`;
}

function applicantType(caseAssociate) {
    if (read(caseAssociate, "is_main_borrower")) {
        return "Main Borrower";
    }
    if (read(caseAssociate, "is_authorized_signatory")) {
        return "Authorized Signatory";
    }
    return "Co-applicant";
}

function bureauLabel(applicableBureau) {
    return Number(applicableBureau) === 2 ? "CRIF" : "CIBIL";
}

// Formats a company address record into a single line.
function formatAddress(address) {
    if (!address) {
        return null;
    }
    const city = read(read(address, "city"), "name");
    const state = read(read(address, "state"), "name");
    const parts = [
        read(address, "address_line_1"),
        read(address, "address_line_2"),
        city,
        state,
        read(address, "pincode")
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
}

// Address-type ids (constants/enums ADDRESSES_TYPES): Residence=5, Permanent=6.
const ADDR_RESIDENCE = 5;
const ADDR_PERMANENT = 6;

// Immutable record -> plain object (the address edit form needs plain values).
function toPlainRecord(record) {
    if (!record) {
        return null;
    }
    return typeof record.toJS === "function" ? record.toJS() : record;
}

/**
 * Strictly resolves ONE address of the given type for an associate, returning
 * the raw record (or null).
 *
 * Deliberately strict: it never falls back to another address_type and never
 * picks up a company-level address (registered/operating), so a Residence cell
 * can't silently render a Permanent address — or the company's office.
 *
 * @param {*} fullAssociate    company-associate record
 * @param {Array} companyAddresses  company.addresses (all types, all associates)
 * @param {number|string} associateId
 * @param {number} type        ADDR_RESIDENCE (5) | ADDR_PERMANENT (6)
 * @param {string} directKey   "residence_address" | "permanent_address"
 */
function findAddressRecord(
    fullAssociate,
    companyAddresses,
    associateId,
    type,
    directKey
) {
    // The API sometimes populates residence_address/permanent_address directly
    // on the associate (nested address object) — use that first.
    const direct = read(fullAssociate, directKey);
    if (direct) {
        return direct;
    }
    const pools = [
        toArray(read(fullAssociate, "addresses")),
        companyAddresses.filter(
            addr => Number(read(addr, "associate")) === Number(associateId)
        )
    ];
    for (let i = 0; i < pools.length; i += 1) {
        const pool = pools[i];
        if (!pool || !pool.length) {
            continue;
        }
        const hit = pool.find(
            addr => Number(read(addr, "address_type")) === type
        );
        if (hit) {
            return hit;
        }
    }
    return null;
}

/**
 * Builds the applicant view-models from the various redux/service sources.
 *
 * @param {Object} sources
 * @param {*} sources.caseAssociates  Immutable List of case-associate records
 * @param {*} sources.company         company record (for addresses)
 * @param {*} sources.documents       redux `documents` slice (case_documents)
 * @param {Array} sources.cibilDocs   results from CompanyService.getCibilDocuments
 * @param {Array} sources.bureauRecords  fetchCaseCibilAllApplicants results
 * @returns {Array} applicant view-models
 */
// Mirrors backend generics.utils.enums.RelationshipWithBorrower (__labels__).
// The associate-list API returns relationship_with_borrower as its integer value.
const RELATIONSHIP_WITH_BORROWER_LABELS = {
    0: "Undefined",
    1: "Father",
    2: "Mother",
    3: "Spouse",
    4: "Brother",
    5: "Sister",
    6: "Friend",
    7: "Colleague",
    8: "Other",
    9: "Son",
    10: "Daughter",
    11: "Nephew",
    12: "Niece",
    13: "Uncle",
    14: "Aunt",
    15: "Brother in Law",
    16: "Sister in Law"
};

// Resolves the relationship to a display label. The API may return it as the
// enum integer (1-8), a numeric string, a label ("Spouse"), an enum name
// ("SPOUSE") or a qualified name ("RelationshipWithBorrower.SPOUSE"). 0 /
// "Undefined" / missing render as no meaningful relationship.
function relationshipWithBorrowerLabel(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    // Numeric enum value (or numeric string).
    if (typeof value === "number" || /^-?\d+$/.test(String(value).trim())) {
        const num = Number(value);
        return num ? RELATIONSHIP_WITH_BORROWER_LABELS[num] || null : null;
    }
    // String — normalise an enum name / qualified name to a label.
    let str = String(value).trim();
    if (str.indexOf(".") !== -1) {
        str = str.split(".").pop();
    }
    if (!str || /^undefined$/i.test(str)) {
        return null;
    }
    const match = Object.values(RELATIONSHIP_WITH_BORROWER_LABELS).find(
        label => label.toLowerCase() === str.toLowerCase()
    );
    if (match) {
        return match;
    }
    // Fallback: prettify the raw token (e.g. "SPOUSE" -> "Spouse").
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Numeric enum value backing the Relation <Select>. Accepts every shape
 * relationshipWithBorrowerLabel() does, and returns null for
 * absent/0/"Undefined" so the picker shows its placeholder rather than
 * pretending "Undefined" is a real choice.
 */
function relationshipWithBorrowerValue(value) {
    const label = relationshipWithBorrowerLabel(value);
    if (!label) {
        return null;
    }
    const key = Object.keys(RELATIONSHIP_WITH_BORROWER_LABELS).find(
        k => RELATIONSHIP_WITH_BORROWER_LABELS[k] === label
    );
    return key === undefined ? null : Number(key);
}

// Options for the Relation picker. UNDEFINED (0) is omitted — clearing the
// Select sends 0 to reset it (the API treats 0 as UNDEFINED, and only skips
// the field when it is null). "Other" is a catch-all, so it sorts last rather
// than sitting mid-list where its enum value (8) would otherwise place it.
export const RELATIONSHIP_WITH_BORROWER_UNDEFINED = 0;
export const RELATIONSHIP_WITH_BORROWER_OTHER = 8;
export const RELATIONSHIP_WITH_BORROWER_OPTIONS = Object.keys(
    RELATIONSHIP_WITH_BORROWER_LABELS
)
    .map(Number)
    .filter(value => value !== RELATIONSHIP_WITH_BORROWER_UNDEFINED)
    .sort((a, b) => {
        if (a === RELATIONSHIP_WITH_BORROWER_OTHER) {
            return 1;
        }
        if (b === RELATIONSHIP_WITH_BORROWER_OTHER) {
            return -1;
        }
        return a - b;
    })
    .map(value => ({
        value,
        label: RELATIONSHIP_WITH_BORROWER_LABELS[value]
    }));

export function buildApplicants({
    caseAssociates,
    company,
    documents,
    companyDocuments,
    cibilDocs,
    bureauRecords
}) {
    const caseAssociateList = toArray(caseAssociates);
    if (!caseAssociateList.length) {
        return [];
    }
    // KYC docs (photo / PAN / Aadhaar) can be filed under either the case
    // documents or the company documents slice — merge both so a doc is found
    // wherever it lives.
    const docList = documentRecordsToArray(documents).concat(
        documentRecordsToArray(companyDocuments)
    );
    const cibilList = cibilDocs || [];
    const bureauList = bureauRecords || [];
    const companyAddresses = toArray(read(company, "addresses"));

    // The case-associate's `company_associate` is a slim snapshot; the rich
    // fields (residence_address, email, aadhaar, father_name) live on the full
    // company.associates record. Index those by id to enrich.
    const companyAssocById = {};
    toArray(read(company, "associates")).forEach(assoc => {
        const id = read(assoc, "id");
        if (id !== null && id !== undefined) {
            companyAssocById[id] = assoc;
        }
    });

    const built = caseAssociateList.map((caseAssociate, index) => {
        const slim = read(caseAssociate, "company_associate");
        const associateId =
            read(slim, "id") || read(caseAssociate, "company_associate_id");
        // Prefer the full associate record, fall back to the slim snapshot.
        const full = companyAssocById[associateId] || slim;
        const get = (record, keys) => {
            const v = firstNonEmpty(record, keys);
            return v !== undefined ? v : firstNonEmpty(slim, keys);
        };
        const firstName = get(full, ["first_name"]) || "";
        const lastName = get(full, ["last_name"]) || "";
        const dob = get(full, ["dob"]);
        const gender = get(full, ["gender"]);
        const pan = get(full, ["pan"]);
        const aadhaar = get(full, ["aadhar", "aadhaar", "aadhar_number"]);
        const email = get(full, ["email", "email_2"]);
        // % of the company held by this applicant. Comes through as a decimal
        // string ("100.00"); 0 is a legitimate value, so only null/"" is "unset".
        const shareholding = get(full, ["shareholding"]);
        const phone = get(full, [
            "phone",
            "phone_office",
            "phone_residence"
        ]);
        const fatherName = get(full, [
            "father_name",
            "fathers_name",
            "father"
        ]);
        // relationship_with_borrower is saved on the case-associate AND the
        // company-associate — read the case-associate first, then fall back to
        // the company-associate (full/slim) so it maps wherever the API sends it.
        const relCase = firstNonEmpty(caseAssociate, [
            "relationship_with_borrower"
        ]);
        const relationshipValue =
            relCase !== undefined
                ? relCase
                : get(full, ["relationship_with_borrower"]);
        // Addresses come from the addresses list (by address_type + associate
        // id), the same source the Case Details tab renders. Residence and
        // Permanent are resolved independently — neither falls back to the
        // other.
        const residenceRecord = findAddressRecord(
            full,
            companyAddresses,
            associateId,
            ADDR_RESIDENCE,
            "residence_address"
        );
        const permanentRecord = findAddressRecord(
            full,
            companyAddresses,
            associateId,
            ADDR_PERMANENT,
            "permanent_address"
        );
        const address = formatAddress(residenceRecord);
        const permanentAddress = formatAddress(permanentRecord);
        const age = dob ? moment().diff(dob, "years") : null;

        const docsByTile = {
            photo: findDoc(docList, associateId, KYC_DOCUMENT_TYPES.PHOTO),
            pan: findDoc(docList, associateId, KYC_DOCUMENT_TYPES.PAN),
            aadhaar: findDoc(docList, associateId, KYC_DOCUMENT_TYPES.AADHAAR)
        };

        // Bureau: prefer the all-applicants record (has file_url + pulled date),
        // fall back to the CIBIL document (doc 51).
        const bureauRecord = bureauList.find(
            rec =>
                Number(read(read(rec, "associate"), "id")) ===
                    Number(associateId) && Number(read(rec, "bureau_type")) === 1
        );
        const reportDoc = cibilList.find(
            doc =>
                Number(doc.associate) === Number(associateId) &&
                Number(doc.document) === BUREAU_DOCUMENT_TYPES.REPORT
        );
        const cibilScore = readCaseLevel(caseAssociate, "cibil_score");
        const reportUrl =
            asPath(bureauRecord && read(bureauRecord, "file_url")) ||
            asPath(reportDoc && reportDoc.file) ||
            null;
        const pulledDate = bureauRecord && read(bureauRecord, "created_date");
        const bureauName = bureauLabel(
            readCaseLevel(caseAssociate, "applicable_bureau")
        );
        const band = scoreBand(cibilScore);
        const hasBureau =
            cibilScore || readCaseLevel(caseAssociate, "personal_cibil_score") ||
            reportUrl;

        return {
            id: associateId || `applicant-${index}`,
            // Case-associate record id — the key the bureau-remark save (PUT
            // /case/add-remove-associates/) updates against.
            caseAssociateId: read(caseAssociate, "id") || null,
            name: `${firstName} ${lastName}`.trim(),
            firstName,
            lastName,
            fatherName,
            type: applicantType(caseAssociate),
            isMainBorrower: read(caseAssociate, "is_main_borrower") || false,
            relationshipWithBorrower: relationshipWithBorrowerLabel(
                relationshipValue
            ),
            // Numeric enum backing the editable Relation picker.
            relationshipValue: relationshipWithBorrowerValue(relationshipValue),
            pan,
            aadhaar,
            shareholding: formatShareholding(shareholding),
            email,
            phone,
            address,
            residenceAddress: address,
            permanentAddress,
            // Raw records + associate id so the address edit form can prefill
            // and POST an update (null record => create a new one). Same
            // shape for Residence and Permanent so a single modal handles both.
            residenceAddressRecord: toPlainRecord(residenceRecord),
            permanentAddressRecord: toPlainRecord(permanentRecord),
            associateId: associateId || null,
            dob,
            age,
            dobLabel: dob ? moment(dob).format("DD MMM YYYY") : null,
            dobAndAge: dob ? `${moment().diff(dob, "years")} years` : null,
            docsByTile,
            gender,
            verification: {
                pan: Boolean(docsByTile.pan || pan),
                aadhaar: Boolean(docsByTile.aadhaar || aadhaar),
                photo: Boolean(docsByTile.photo),
                mobile: Boolean(phone),
                email: Boolean(email),
                address: Boolean(address)
            },
            bureau: hasBureau
                ? {
                    bureau: bureauName,
                    cibil_score: cibilScore,
                    band,
                    personal_cibil_score: readCaseLevel(
                        caseAssociate,
                        "personal_cibil_score"
                    ),
                    cibil_remarks: readCaseLevel(caseAssociate, "cibil_remarks"),
                    report_url: reportUrl,
                    // Structured bureau sections ({ key: { label, raw_html } })
                    // rendered inline so analysts verify without the PDF.
                    all_data:
                        (bureauRecord && read(bureauRecord, "all_data")) || null,
                    pulled_date: pulledDate
                        ? moment(pulledDate).format("DD MMM YYYY")
                        : null
                }
                : null
        };
    });

    // No reordering — applicants stay in the order the API returned them. (They
    // used to be sorted main-borrower-first, which made a row appear to jump when
    // the flag was toggled.)
    return built;
}

// Formats a date (moment or string) for display, or null when invalid/absent.
function dateLabel(value) {
    if (!value) {
        return null;
    }
    const m = moment.isMoment(value) ? value : moment(value);
    return m.isValid() ? m.format("DD MMM YYYY") : null;
}

// Whole years between a date and now, or null when invalid/absent.
function yearsSince(value) {
    if (!value) {
        return null;
    }
    const m = moment.isMoment(value) ? value : moment(value);
    if (!m.isValid()) {
        return null;
    }
    const years = moment().diff(m, "years");
    return years >= 0 ? years : null;
}

/**
 * Builds the Company Detail view-model: identity facts (GSTIN, PAN, constitution,
 * vintage, company CIBIL), operating + registered addresses, and the company
 * verification documents (GST certificate, utility bill, shop photo).
 *
 * @param {Object} sources
 * @param {*} sources.company           company record (addresses + identity)
 * @param {*} sources.documents         case_documents slice
 * @param {*} sources.companyDocuments  company_documents slice (GST cert,
 *                                      utility bill, shop photo live here)
 * @returns {Object|null} company view-model, or null when the company is absent
 */
// Builds the "Last GST return filed" label from the case-detail-fields
// fetch_filing_record.latest_return — dateOfFiling ("DD-MM-YYYY") plus the
// returnPeriod ("MMYYYY") rendered as a readable month, e.g. "15 Jun 2026 (May 2026)".
function gstFilingLabelFromRecord(fetchFilingRecord) {
    const record =
        fetchFilingRecord && fetchFilingRecord.toJS
            ? fetchFilingRecord.toJS()
            : fetchFilingRecord;
    const latest = record && record.latest_return;
    if (!latest) {
        return null;
    }
    const filedOn = latest.dateOfFiling
        ? moment(latest.dateOfFiling, "DD-MM-YYYY", true)
        : null;
    const filedLabel =
        filedOn && filedOn.isValid()
            ? filedOn.format("DD MMM YYYY")
            : latest.dateOfFiling || null;
    // returnPeriod is MMYYYY (e.g. "052026").
    const period =
        latest.returnPeriod && String(latest.returnPeriod).length === 6
            ? moment(String(latest.returnPeriod), "MMYYYY", true)
            : null;
    const periodLabel =
        period && period.isValid()
            ? period.format("MMM YYYY")
            : latest.returnPeriod || null;
    // Filing frequency (Monthly / Quarterly) — prefer latest_return.frequency,
    // fall back to the record-level return_frequency.
    const frequency = latest.frequency || record.return_frequency || null;
    let label = null;
    if (filedLabel && periodLabel) {
        label = `${filedLabel} (${periodLabel})`;
    } else {
        label = filedLabel || periodLabel || null;
    }
    if (label && frequency) {
        return `${label} · ${frequency}`;
    }
    return label || frequency || null;
}

export function buildCompany({
    company,
    documents,
    companyDocuments,
    caseDetail,
    fetchFilingRecord
}) {
    if (!company) {
        return null;
    }
    // Company-level KYC docs live in the company_documents slice; case docs in
    // case_documents. Merge both so a doc is found wherever it was filed.
    const docList = documentRecordsToArray(companyDocuments).concat(
        documentRecordsToArray(documents)
    );

    const companyAddresses = toArray(read(company, "addresses"));

    // Owned / Rented label from the address ownership_type (1=Owned, 2=Rented).
    const ownershipLabel = addr => {
        const type = read(addr, "ownership_type");
        return type ? RESIDENCE_TYPES.flip().get(Number(type)) || null : null;
    };

    // Match on address_type only — exactly like the CompanyRecord
    // registered_address / operating_address getters the Case Details tab uses.
    // (Company addresses can carry a non-null `associate`, so we must NOT filter
    // on it or they get excluded.) Returns the formatted line + ownership label.
    const addressByType = type => {
        const match = companyAddresses.find(
            addr => Number(read(addr, "address_type")) === type
        );
        return {
            text: formatAddress(match),
            ownership: match ? ownershipLabel(match) : null,
            // Raw record so the edit form can pre-fill and, crucially, carry the
            // existing row's id — without it a save creates a second address
            // instead of updating this one.
            record: match
                ? typeof match.toJS === "function"
                    ? match.toJS()
                    : match
                : null
        };
    };

    // Every extra address the company carries beyond registered/operating. The
    // API key is `additional_addresess` (sic) and its entries are already
    // FORMATTED STRINGS, e.g. "Ground, 7, Thandarai koot road, …, 603403" — not
    // address objects. An object entry is still tolerated (formatAddress) in
    // case the payload is ever normalised. Blank entries are skipped so we never
    // render an empty card. NOTE the key must also be declared on the
    // CompanyRecord (records/company-record.js) or Immutable drops it.
    const additionalAddresses = toArray(
        read(company, "additional_addresess") ||
            read(company, "additional_addresses")
    )
        .map((addr, index) => {
            const text =
                typeof addr === "string" ? addr.trim() : formatAddress(addr);
            if (!text) {
                return null;
            }
            return {
                key: (typeof addr === "string" ? null : read(addr, "id")) || index,
                text,
                ownership:
                    typeof addr === "string" ? null : ownershipLabel(addr)
            };
        })
        .filter(Boolean);

    const constitutionId = read(company, "constitution");
    const constitution =
        constitutionId !== null && constitutionId !== undefined
            ? CONSTITUTION_TYPES.findKey(
                value => Number(value) === Number(constitutionId)
            ) || null
            : null;

    // Vintage: prefer business start date, fall back to incorporation.
    const businessStart = read(company, "business_start_date");
    const incorporation = read(company, "date_of_incorporation");
    const vintageYears =
        yearsSince(businessStart) !== null
            ? yearsSince(businessStart)
            : yearsSince(incorporation);

    const companyCibil =
        read(company, "company_cibil_score") || null;

    // Product type: business_product_type is an id — map it to its label
    // (the same value the Case Details "Product Details" card shows).
    const productTypeId = read(caseDetail, "business_product_type");
    const productType =
        productTypeId !== null &&
        productTypeId !== undefined &&
        productTypeId !== ""
            ? BUSINESS_PRODUCT_TYPE.findKey(
                v => Number(v) === Number(productTypeId)
            ) || null
            : null;

    // ASM / RM: "<name> (<distance> · <vintage> mo)" from caseDetail's
    // current_asm_details / current_rm_details. Vintage (months) is only on ASM;
    // RM has no vintage, so it degrades to "<name> (<distance>)".
    const formatContact = details => {
        if (!details) {
            return null;
        }
        const name = details.full_name;
        if (!name) {
            return null;
        }
        const parts = [];
        if (details.distance_text) {
            parts.push(details.distance_text);
        }
        if (details.vintage !== null && details.vintage !== undefined) {
            parts.push(`${details.vintage} mo`);
        }
        return parts.length ? `${name} (${parts.join(" · ")})` : name;
    };
    const asm = formatContact(read(caseDetail, "current_asm_details"));
    const rm = formatContact(read(caseDetail, "current_rm_details"));

    // Re-assignment candidates for the ASM: the pincode-nearest ASMs off
    // case/<id>/. NOTE `nearest_asm_details` must be declared on the CaseDetail
    // Record (records/case-record.js) — an Immutable Record silently drops keys
    // it does not declare, which reads as "API sent nothing".
    const asmOptions = toArray(read(caseDetail, "nearest_asm_details"))
        .map(option => {
            const id = read(option, "id");
            const name =
                read(option, "full_name") || read(option, "user_first_name");
            if (id === null || id === undefined || !name) {
                return null;
            }
            const bits = [];
            const distance = read(option, "distance_text");
            const km = read(option, "distance_km");
            if (distance) {
                bits.push(distance);
            } else if (km !== null && km !== undefined) {
                bits.push(`${km} km`);
            }
            const pincode = read(option, "pincode");
            if (pincode) {
                bits.push(String(pincode));
            }
            return {
                id,
                name,
                label: bits.length ? `${name} (${bits.join(" · ")})` : name
            };
        })
        .filter(Boolean);

    // Resolve docs for the base tiles plus the constitution-specific ones
    // (Company PAN / Partnership Deed / Partners list). The constitution-specific
    // tiles are only *rendered* for the relevant constitutions (see
    // company-detail-section), but resolving them here is harmless when not shown.
    const docs = {};
    COMPANY_DOCUMENT_TILES.forEach(tile => {
        // `anyDoc` tiles (e.g. the takeover document) are not company-KYC docs:
        // they can be filed against the case or an applicant, so they must not
        // go through findCompanyDoc's company-level-only restriction.
        //
        // Utility Bill: docList's order comes from an Immutable `Map` keyed by
        // document id (see FETCHED_COMPANY_DOCUMENTS in documents-reducer.js),
        // whose iteration order tends to come out ascending by id — i.e. oldest
        // upload first — regardless of what order the API returned. A plain
        // `.find()` would then pick a stale re-upload over a newer one, so this
        // tile searches the reversed list to prefer the latest match instead.
        const searchList =
            tile.key === "utilityBill" ? docList.slice().reverse() : docList;
        docs[tile.key] = tile.anyDoc
            ? findAnyDoc(searchList, tile.match)
            : findCompanyDoc(searchList, tile.match);
    });
    docs[COMPANY_PAN_TILE.key] = findCompanyDoc(docList, COMPANY_PAN_TILE.match);
    docs[PARTNERSHIP_DEED_TILE.key] = findCompanyDoc(
        docList,
        PARTNERSHIP_DEED_TILE.match
    );
    docs[PARTNERS_LIST_TILE.key] = findCompanyDoc(
        docList,
        PARTNERS_LIST_TILE.match
    );

    // Industry / Anchor / Seller mirror the top Case Profile strip. They live on
    // the CASE, not the company.
    //
    // CaseDetailRecord carries BOTH a singular `anchor`/`seller` and the plural
    // `anchors`/`sellers` lists, and its `anchor_name`/`seller_name` getters read
    // the SINGULAR ones — which the case-detail API leaves null. So read the
    // lists (a case can have several of each, as the Case Profile tooltip shows)
    // and only fall back to the getters.

    // Match the base-page Case Profile's data SOURCE exactly
    // (get_anchors_user_view / get_sellers_user_view in case-details-summary.js):
    //   anchors = supply_chains.map(sc => sc.anchor)  (preferred, when non-empty)
    //             else caseDetail.anchors             (fallback)
    // then render each entry as `${name}${aligned}${id}`, joined with ", ".
    // Credit Checker previously read the case-level anchors/sellers only, which
    // are empty/different from the supply-chain anchor/seller the base shows.
    const supplyChains = toArray(read(caseDetail, "supply_chains"));
    const scEntities = prop =>
        supplyChains.map(sc => read(sc, prop)).filter(Boolean);

    const formatAligned = entries =>
        (entries || [])
            .map(entry => {
                const name = read(entry, "name");
                if (!name) return null;
                const aligned = read(entry, "is_anchor_aligned")
                    ? "(Aligned)"
                    : "(Not Aligned)";
                const id = read(entry, "id");
                return `${name}${aligned}${id != null ? id : ""}`;
            })
            .filter(Boolean)
            .join(", ") || null;

    const scAnchors = scEntities("anchor");
    const scSellers = scEntities("seller");
    const anchorEntries = scAnchors.length
        ? scAnchors
        : toArray(read(caseDetail, "anchors"));
    const sellerEntries = scSellers.length
        ? scSellers
        : toArray(read(caseDetail, "sellers"));

    const industry =
        read(caseDetail, "industry") || read(caseDetail, "industry_name") || null;
    const anchorName =
        formatAligned(anchorEntries) || readProp(caseDetail, "anchor_name") || null;
    // Alignment is now inline in the name string above, so no separate suffix.
    const anchorAlignment = null;
    const sellerName =
        formatAligned(sellerEntries) || readProp(caseDetail, "seller_name") || null;
    const sellerAlignment = null;

    // Date the seller was appointed by the anchor — sits on the active
    // supply-chain row (CompanySupplychain.date_of_appointment). Reads the
    // first case_supply_chain entry that carries a value so the same field
    // credit-appraisal-tab shows appears in Company Details too.
    const scRows = toArray(read(caseDetail, "case_supply_chain"));
    const rawAppointment = scRows
        .map(sc => read(sc, "date_of_appointment"))
        .find(v => v);
    const anchorAppointmentLabel = rawAppointment ? dateLabel(rawAppointment) : null;

    // Case Type: the Case Profile shows the SANCTIONED type, except a Renewal,
    // which it spells out as "Existing (Renewal)". Mirrored so the two agree.
    const rawCaseType = read(caseDetail, "case_type");
    const caseType =
        rawCaseType === CASE_CATEGORY_TYPES.get("Renewal")
            ? "Existing (Renewal)"
            : read(caseDetail, "case_type_sanctioned") || null;
    // Bank is shown by its short_name (e.g. "Progfin"), with the full name on
    // hover. Falls back to the code, then the name, so the row is never blank.
    const bankRecord = read(caseDetail, "bank");
    const bank =
        read(bankRecord, "short_name") ||
        read(bankRecord, "code") ||
        read(bankRecord, "name") ||
        null;
    const bankName = read(bankRecord, "name") || null;

    // Product Details fields — sourced off the CASE's scheme, mirroring the
    // Pricing card so header + Pricing agree. Same label maps the Case Details
    // tab uses for the enum-id fields (business_product_type / program_type)
    // so we render "Short Term Loan (STL)" not "9", "Distributor Others" not
    // "5". Scheme Type reads product_scheme_type (a string), which matches the
    // Case Details "Product Details" card.
    const PRODUCT_TYPE_LABELS = {
        1: "Invoice Finance",
        2: "Vendor Finance",
        3: "Working Capital Demand Loan",
        5: "Purchase Invoice Finance",
        6: "Sales Invoice Finance",
        7: "Term Loan",
        8: "Personal Term Loan",
        9: "Short Term Loan (STL)",
        10: "CEP"
    };
    const PROGRAM_TYPE_LABELS = {
        0: "--",
        1: "Primary",
        2: "Secondary",
        3: "Anchor-Led Distributor",
        4: "DB-Led Retailer",
        5: "Sales-Led Distributor",
        6: "Sales-Led Retailer"
    };
    const businessProductTypeId = read(caseDetail, "business_product_type");
    // Prefer the enum-mapped label; fall back to Company.product_type text
    // when the case doesn't carry a business_product_type (older / draft cases).
    const productTypeResolved =
        (businessProductTypeId !== null && businessProductTypeId !== undefined
            ? PRODUCT_TYPE_LABELS[Number(businessProductTypeId)]
            : null) || productType || null;
    const programTypeId = read(caseDetail, "program_type");
    const programTypeResolved =
        programTypeId !== null && programTypeId !== undefined
            ? PROGRAM_TYPE_LABELS[Number(programTypeId)] || String(programTypeId)
            : null;
    // scheme_type field carries "Invoice Finance" / "Term Loan" / "Factoring" —
    // Case Details renders "Factoring" as "CEP" for display parity. Match it.
    const schemeTypeRaw = read(caseDetail, "product_scheme_type") || read(caseDetail, "scheme_type") || null;
    const schemeType =
        schemeTypeRaw === "Factoring" ? "CEP" : schemeTypeRaw || null;

    // ROI + Tenure — reuse buildPricing's exact logic (defined below in this
    // module). We inline the minimal read here to avoid a circular dependency
    // and stale-string mismatch. Same source: caseDetail.scheme + term-loan
    // child.
    const scheme = read(caseDetail, "scheme");
    const schemeGet = key =>
        scheme && (scheme.get ? scheme.get(key) : scheme[key]);
    let roiNum = _posNumber(schemeGet && schemeGet("roi"));
    if (roiNum === null && schemeGet) {
        const sched = schemeGet("roi_schedule");
        const first = sched && (sched[0] || (sched.get && sched.get(0)));
        if (first) roiNum = _posNumber(first.roi || (first.get && first.get("roi")));
    }
    const roi = roiNum !== null ? `${_formatRate(roiNum)}%` : null;
    let tenure = null;
    const tlList = schemeGet && schemeGet("scheme_scheme_term_loan");
    const termLoan =
        tlList && ((tlList.get && tlList.get(0)) || (Array.isArray(tlList) ? tlList[0] : null));
    if (termLoan) {
        const months = _posNumber(
            termLoan.get ? termLoan.get("max_instalment_count") : termLoan.max_instalment_count
        );
        if (months !== null) tenure = `${Math.round(months)} Month(s)`;
    }
    if (!tenure && schemeGet) {
        const tenor = _posNumber(schemeGet("tenor"));
        if (tenor !== null && tenor < 9999) tenure = `${Math.round(tenor)} days`;
        else {
            const t = schemeGet("tenure");
            if (t) tenure = String(t);
        }
    }

    // Reporting Industry / Reporting Anchor — kept as separate case-level
    // fields (distinct from industry / anchorName which are the business's
    // own). Both carry {id, name}; expose the name for display and the id
    // for the edit modal to pre-select.
    const reportingIndustryRec = read(caseDetail, "reporting_industry");
    const reportingAnchorRec = read(caseDetail, "reporting_anchor");
    const reportingIndustry =
        (reportingIndustryRec && read(reportingIndustryRec, "name")) || null;
    const reportingIndustryId =
        (reportingIndustryRec && read(reportingIndustryRec, "id")) || null;
    const reportingAnchor =
        (reportingAnchorRec && read(reportingAnchorRec, "name")) || null;
    const reportingAnchorId =
        (reportingAnchorRec && read(reportingAnchorRec, "id")) || null;

    return {
        name: read(company, "name") || null,
        legalName: read(company, "legal_name") || null,
        tradeName: read(company, "trade_name") || null,
        otherTradeName: read(company, "other_trade_name") || null,
        gstin: read(company, "gstin") || null,
        gstinStatus: read(company, "gstin_status") || null,
        pan: read(company, "pan") || null,
        industry,
        anchorName,
        anchorAlignment,
        anchorAppointmentLabel,
        sellerName,
        sellerAlignment,
        reportingIndustry,
        reportingIndustryId,
        reportingAnchor,
        reportingAnchorId,
        caseType,
        bank,
        bankName,
        constitution,
        productType: productTypeResolved,
        roi,
        tenure,
        schemeType,
        programType: programTypeResolved,
        asm,
        asmOptions,
        rm,
        companyCibil,
        companyCibilBand: scoreBand(companyCibil),
        cibilRemarks: read(company, "company_cibil_remarks") || null,
        incorporationLabel: dateLabel(incorporation),
        vintageYears,
        // Prefer the live case-detail-fields filing record (fetch_filing_record
        // .latest_return); fall back to the company's stored filing date.
        gstinFilingLabel:
            gstFilingLabelFromRecord(fetchFilingRecord) ||
            dateLabel(
                firstNonEmpty(company, [
                    "date_of_gstin_return_filing",
                    "date_of_gstin_return_filling"
                ])
            ),
        operatingAddress: addressByType(COMPANY_ADDRESS_TYPES.OPERATING),
        registeredAddress: addressByType(COMPANY_ADDRESS_TYPES.REGISTERED),
        additionalAddresses,
        docs,
        // City / State / Pincode surfaced for the Case Profile header row —
        // pulled off the REGISTERED address first to match the Case Details
        // ("base page") Case Profile, which reads the registered address
        // (address_type == 1); operating is only a fallback when there's no
        // registered address on file. The negative-pincode flag lives on the
        // pincode master; a lot of cases don't carry it inline, so it's null
        // when unavailable.
        city:
            addressCity(addressByType(COMPANY_ADDRESS_TYPES.REGISTERED)) ||
            addressCity(addressByType(COMPANY_ADDRESS_TYPES.OPERATING)),
        state:
            addressState(addressByType(COMPANY_ADDRESS_TYPES.REGISTERED)) ||
            addressState(addressByType(COMPANY_ADDRESS_TYPES.OPERATING)),
        pincode:
            addressPincode(addressByType(COMPANY_ADDRESS_TYPES.REGISTERED)) ||
            addressPincode(addressByType(COMPANY_ADDRESS_TYPES.OPERATING)),
        pincodeCategory:
            addressPincodeCategory(
                addressByType(COMPANY_ADDRESS_TYPES.REGISTERED)
            ) ||
            addressPincodeCategory(
                addressByType(COMPANY_ADDRESS_TYPES.OPERATING)
            ),
        // Case Proposed Limit — same value the base-page Case Profile shows
        // (caseDetails.data.proposed_limit).
        proposedLimit: read(caseDetail, "proposed_limit"),
        // Insurance amortisation — mirrors the "+ ₹X (Insurance)" line the
        // Case Details tab renders under Case Proposed Limit. Amount is
        // rendered only when the applicable flag is on.
        insuranceApplicable: Boolean(
            read(caseDetail, "is_insurance_amortisation_applicable")
        ),
        insuranceAmount: read(caseDetail, "insurance_amortisation_amount")
    };
}

// Small readers over the address structure that data-utils' `addressByType`
// returns. `raw` is the raw address record; `text` is the flattened text
// (city, state, pincode). We prefer structured fields when available, then
// fall back to parsing pincode out of the text.
// `read()` doesn't walk dotted paths, so these helpers do the two-step read
// manually. The address record's city / state are nested objects with a
// `name` field (formatAddress does the exact same walk).
function addressCity(addr) {
    if (!addr) return null;
    const raw = addr.record || addr.raw || addr;
    const cityNode = read(raw, "city");
    const nested = read(cityNode, "name");
    if (nested) return nested;
    if (typeof cityNode === "string") return cityNode;
    return read(raw, "city_name") || null;
}
function addressState(addr) {
    if (!addr) return null;
    const raw = addr.record || addr.raw || addr;
    const stateNode = read(raw, "state");
    const nested = read(stateNode, "name");
    if (nested) return nested;
    if (typeof stateNode === "string") return stateNode;
    return read(raw, "state_name") || null;
}
function addressPincode(addr) {
    if (!addr) return null;
    const raw = addr.record || addr.raw || addr;
    const pinNode = read(raw, "pincode");
    // pincode is usually just a string/number on the address; when it's a
    // nested pincode-master object, its .name / .pincode field carries the
    // 6-digit code.
    if (pinNode && typeof pinNode !== "object") return String(pinNode);
    if (pinNode) {
        const nested = read(pinNode, "pincode") || read(pinNode, "name");
        if (nested) return String(nested);
    }
    // Fallback: parse trailing 6 digits out of the flattened text.
    const text = addr.text || "";
    const m = String(text).match(/(\d{6})\b/);
    return m ? m[1] : null;
}
// Returns a RAG category for the pincode: "RED" / "AMBER" / "GREEN" — or
// null when nothing is knowable. Sources, in order:
//   1. explicit `pincode_category` / `pincode.category` (Pincode master's
//      three-level flag: UNDEFINED / RED / AMBER — UNDEFINED reads as GREEN
//      when onboarding is allowed).
//   2. `is_negative_pincode` boolean → true = RED.
//   3. `pincode.is_allowed_for_onboarding` → false = RED, true = GREEN.
function addressPincodeCategory(addr) {
    if (!addr) return null;
    const raw = addr.record || addr.raw || addr;
    const pinNode = read(raw, "pincode");
    // Case 1: explicit category on the pincode master.
    const catRaw =
        read(raw, "pincode_category") ||
        read(pinNode, "category") ||
        null;
    if (catRaw) {
        const up = String(catRaw).toUpperCase();
        if (up === "RED") return "RED";
        if (up === "AMBER") return "AMBER";
        // UNDEFINED is the master's default — treat as GREEN when the
        // pincode is allowed for onboarding, otherwise RED.
        if (up === "UNDEFINED") {
            const allowed = read(pinNode, "is_allowed_for_onboarding");
            if (allowed === false) return "RED";
            return "GREEN";
        }
    }
    // Case 2: legacy is_negative_pincode.
    const explicit = read(raw, "is_negative_pincode");
    if (typeof explicit === "boolean") {
        return explicit ? "RED" : "GREEN";
    }
    // Case 3: onboarding-allowed boolean.
    const allowed = read(pinNode, "is_allowed_for_onboarding");
    if (typeof allowed === "boolean") {
        return allowed ? "GREEN" : "RED";
    }
    return null;
}

/**
 * Resolves the source documents behind the triangulation (GST returns, ledger,
 * bank statement) from the merged company + case document lists.
 *
 * @returns {Object} { gst, ledger, bank } each { file } or null
 */
// Indian-grouped integer string (e.g. 481140778 -> "48,11,40,778"), no symbol.
function formatIndian(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const num = Number(String(value).replace(/[^0-9.-]/g, ""));
    if (!isFinite(num) || Number.isNaN(num)) {
        return null;
    }
    return Math.round(num).toLocaleString("en-IN");
}

// Builds one call-picker entry from a user payload, or null when there is no
// number to dial. Handles both plain API objects and Immutable UserRecords
// (whose `full_name` getter is not reachable via .get(), hence the compose).
function toCallContact(user, id, type) {
    const phone = read(user, "mobile_number");
    if (!phone) {
        return null;
    }
    const composed = [read(user, "first_name"), read(user, "last_name")]
        .filter(Boolean)
        .join(" ")
        .trim();
    return {
        id,
        type,
        name: composed || read(user, "full_name") || type,
        phone: String(phone)
    };
}

/**
 * Progcap-side contacts for the TVR call picker — currently just the case's ASM
 * (caseDetail.sales_manager_user, a UserPublicSerializer payload carrying
 * `mobile_number`). Dropped when there is no number to dial.
 *
 * @returns {Array} [{ id, type, name, phone }] — same shape the call picker
 *                  and `confirmAndCall` expect from an applicant.
 */
export function buildCallContacts({ caseDetail }) {
    return [
        toCallContact(read(caseDetail, "sales_manager_user"), "asm", "ASM")
    ].filter(Boolean);
}

/**
 * Headline Case Summary metrics, sourced from the real supply-chain record +
 * case detail (the same fields the Credit Summary / final-credit-summary tab
 * shows): last-N-months sales, monthly sales, limit-as-per-sales, proposed limit.
 *
 * @returns {Array} [{ label, value }] — value is an Indian-grouped number string
 */
export function buildTopMetrics({ supplyChainRecord, caseDetail, dataComparison }) {
    if (!supplyChainRecord) {
        return [];
    }
    const scGet = key =>
        supplyChainRecord.get
            ? supplyChainRecord.get(key)
            : supplyChainRecord[key];
    const cdGet = key => {
        if (!caseDetail) {
            return undefined;
        }
        return caseDetail.get ? caseDetail.get(key) : caseDetail[key];
    };

    const netSales = scGet("net_sales_amount") || {};
    const months = (netSales && netSales.months) || 12;
    // Tenure now comes from the case's scheme (caseDetail.scheme.tenure); fall
    // back to the legacy top-level caseDetail.tenure when absent. Kept RAW (with
    // any unit text, e.g. "6 Month(s)") so the "Limit as per … day sales" label
    // matches the Credit Summary, which uses caseDetail.tenure verbatim.
    const scheme = cdGet("scheme");
    const schemeTenure =
        scheme && (scheme.get ? scheme.get("tenure") : scheme.tenure);
    const tenure = schemeTenure || cdGet("tenure") || "";

    const salesProposed = cdGet("sales_proposed_limit");
    const proposed = cdGet("proposed_limit");
    let netSalesAmount = netSales && netSales.amount;
    let monthlyNetSales = scGet("avg_monthly_net_sales");
    let ledgerMonths = months;
    // Supply-chain net sales is primary; when it is blank or 0 for a case whose
    // ledger series (data-comparison) actually has values, fall back to that so
    // the tile reflects the ledger instead of a misleading ₹0 / empty.
    const scNetBlank =
        netSalesAmount === null ||
        netSalesAmount === undefined ||
        netSalesAmount === "" ||
        Number(netSalesAmount) === 0;
    const scMonthlyBlank =
        monthlyNetSales === null ||
        monthlyNetSales === undefined ||
        monthlyNetSales === "" ||
        Number(monthlyNetSales) === 0;
    if (scNetBlank && scMonthlyBlank) {
        const fb = _ledgerFromDataComparison(dataComparison);
        if (fb) {
            netSalesAmount = fb.amount;
            monthlyNetSales = fb.monthly;
            ledgerMonths = fb.months;
        }
    }
    const metrics = [];

    // GST Sales topline (multi-row) — silent when no monthly footprint on file.
    const gst = _gstFyBuckets(dataComparison);
    if (gst) {
        // Rolling last-12-months GST turnover, computed server-side by the GST
        // aggregator (dataComparison.gst_sales_last_12_months). Appended last.
        const gst12 =
            dataComparison && dataComparison.gst_sales_last_12_months;
        // Rendered oldest-first (reverse of the underlying YTD/lastFy/before
        // computation order) — "Last 12 Months" below is exempt and always
        // stays appended at the end.
        const rows = [
            { label: gst.before.label + (gst.before.range ? ` (${gst.before.range})` : ""), amount: gst.before.amount, value: formatIndian(gst.before.amount) },
            { label: gst.lastFy.label, amount: gst.lastFy.amount, value: formatIndian(gst.lastFy.amount) },
            { label: "YTD" + (gst.ytd.range ? ` (${gst.ytd.range})` : ""), amount: gst.ytd.amount, value: formatIndian(gst.ytd.amount) }
        ];
        if (gst12 !== null && gst12 !== undefined && gst12 !== "") {
            rows.push({
                label: "Last 12 Months",
                amount: gst12,
                value: formatIndian(gst12)
            });
        }
        metrics.push({ label: "GST Sales", rows });
    }

    // Ledger Sales — clubs Last-N-months and Monthly into one card.
    if (
        (netSalesAmount !== null && netSalesAmount !== undefined && netSalesAmount !== "") ||
        (monthlyNetSales !== null && monthlyNetSales !== undefined && monthlyNetSales !== "")
    ) {
        metrics.push({
            label: "Ledger Sales",
            rows: [
                { label: `Last ${ledgerMonths} Months`, amount: netSalesAmount, value: formatIndian(netSalesAmount) },
                { label: "Monthly", amount: monthlyNetSales, value: formatIndian(monthlyNetSales) }
            ]
        });
    }

    metrics.push(
        {
            label: `Limit as per ${tenure} day sales`
                .replace(/\s+/g, " ")
                .trim(),
            amount: scGet("limit_basis_sales_display"),
            value: formatIndian(scGet("limit_basis_sales_display"))
        },
        {
            label: "Proposed Limit as per 90 percentile",
            amount: scGet("limit_basis_90_pctl"),
            value: formatIndian(scGet("limit_basis_90_pctl"))
        },
        {
            key: "sales_proposed_limit",
            label: "Sales Proposed Limit",
            amount: salesProposed,
            value: formatIndian(salesProposed),
            // Inline-editable at the credit stages (7, 8) — persisted via the
            // case-detail PATCH, not the case-detail-fields PUT.
            editable: true,
            // Sales not having filed an ask is itself worth seeing, so the tile
            // renders a dash rather than being dropped from the card list.
            alwaysShow: true
        },
        {
            key: "proposed_limit",
            label: "Proposed Limit",
            amount: proposed,
            value: formatIndian(proposed),
            editable: true,
            // Case Details tab renders the insurance amortisation amount
            // as a second line under Case Proposed Limit ("+ 105000 (Insurance)").
            // Mirror the same treatment here so the credit-checker Proposed
            // Limit tile agrees. Only set when the applicable flag is on.
            extraLine: cdGet("is_insurance_amortisation_applicable")
                ? "+ ₹" + (formatIndian(
                    cdGet("insurance_amortisation_amount")
                ) || "—") + " (Insurance)"
                : null
        }
    );

    // Every Limit Assessment amount reads in lakhs, so the whole section sits on
    // one scale — a card in Cr next to cards in Lakh reads as smaller than it is.
    // Applied here (rather than per tile) so tiles added later inherit it.
    const inLakhs = metric => ({
        ...metric,
        lakh: true,
        ...(metric.rows
            ? { rows: metric.rows.map(row => ({ ...row, lakh: true })) }
            : {})
    });

    // Tiles with no number/rows are dropped — except ones explicitly marked N.A.
    return metrics
        .filter(
            metric =>
                metric.rows ||
                metric.value !== null ||
                metric.na ||
                metric.alwaysShow
        )
        .map(inLakhs);
}

// Reads a property that may be a computed getter (case-record seller_name,
// buyer_name, anchor_name) OR an Immutable field OR a plain key. Property access
// first so getters resolve, then `.get` for plain Immutable Maps.
function readProp(obj, key) {
    if (obj === null || obj === undefined) {
        return undefined;
    }
    const direct = obj[key];
    if (direct !== undefined) {
        return direct;
    }
    if (typeof obj.get === "function") {
        return obj.get(key);
    }
    return undefined;
}

/**
 * Builds the Recommendation tab view-model: the recommendation document (matched
 * from the merged document lists) plus the key extracts the credit team reads
 * off it — seller name, seller recommended limit, and the buyer it is
 * recommended for (name + GSTIN when present).
 *
 * @returns {Object} { sellerName, sellerRecommendedLimit, buyerName, buyerGstin, doc }
 */
export function buildRecommendation({
    caseDetail,
    company,
    supplyChainRecord,
    documents,
    companyDocuments
}) {
    const docList = documentRecordsToArray(companyDocuments).concat(
        documentRecordsToArray(documents)
    );
    const doc = findAnyDoc(docList, RECOMMENDATION_DOCUMENT_MATCH);

    const sellerName =
        readProp(caseDetail, "seller_name") ||
        (company && company.name) ||
        null;
    const buyerName = readProp(caseDetail, "buyer_name") || null;
    const buyerGstin =
        readProp(readProp(caseDetail, "company"), "gstin") || null;

    const limit =
        readProp(supplyChainRecord, "proposed_limit") ||
        readProp(caseDetail, "proposed_limit");
    const sellerRecommendedLimit =
        limit !== null && limit !== undefined && limit !== ""
            ? "₹" +
              Math.round(
                  Number(String(limit).replace(/[^0-9.-]/g, ""))
              ).toLocaleString("en-IN")
            : null;

    return {
        sellerName,
        sellerRecommendedLimit,
        buyerName,
        buyerGstin,
        doc
    };
}

export function buildTriangulationDocs({ documents, companyDocuments }) {
    const docList = documentRecordsToArray(companyDocuments).concat(
        documentRecordsToArray(documents)
    );
    const docs = {};
    TRIANGULATION_DOCUMENT_TILES.forEach(tile => {
        docs[tile.key] = findAnyDoc(docList, tile.match);
    });
    return docs;
}

// ---------------------------------------------------------------------------
// GST monthly bucketing — YTD (current FY), Last FY, FY before. Mirrors the
// credit-brief PDF's _gst_topline_tile.
// ---------------------------------------------------------------------------
// Ledger Sales fallback from the data-comparison API. The supply-chain record
// (net_sales_amount / avg_monthly_net_sales) is the primary source, but for some
// cases it computes to 0 / blank even though the ledger series in data-comparison
// (mom_data.gst_ledger[].ledger_sales) carries real numbers. When that happens we
// derive Last-N-months + Monthly from the last (up to) 12 months that have a
// ledger_sales value, so the tile reflects the ledger instead of showing ₹0.
function _ledgerFromDataComparison(dataComparison) {
    const rows =
        (dataComparison &&
            dataComparison.mom_data &&
            dataComparison.mom_data.gst_ledger) ||
        [];
    const withLedger = rows.filter(
        r =>
            r &&
            r.ledger_sales !== null &&
            r.ledger_sales !== undefined &&
            r.ledger_sales !== ""
    );
    if (!withLedger.length) return null;
    const last12 = withLedger.slice(-12);
    const total = last12.reduce((s, r) => s + Number(r.ledger_sales || 0), 0);
    if (!(total > 0)) return null;
    return {
        amount: total,
        months: last12.length,
        monthly: total / last12.length
    };
}

function _gstFyBuckets(dataComparison) {
    const months = (dataComparison && dataComparison.common_months_comparison) || [];
    if (!months.length) return null;
    const today = new Date();
    const fyCurStart = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const fyLastStart = fyCurStart - 1;
    const fyBeforeStart = fyCurStart - 2;
    const MONTHS = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
    function inFy(dt, startYear) {
        if (dt.getFullYear() === startYear && dt.getMonth() >= 3) return true;
        if (dt.getFullYear() === startYear + 1 && dt.getMonth() <= 2) return true;
        return false;
    }
    let ytdSum = 0, lastFySum = 0, beforeSum = 0;
    const ytdDates = [], beforeDates = [];
    months.forEach(m => {
        const parts = String(m.month || "").split(" ");
        if (parts.length !== 2) return;
        const mo = MONTHS[parts[0]];
        const yr = parseInt(parts[1], 10);
        if (mo === undefined || Number.isNaN(yr)) return;
        const dt = new Date(yr, mo, 1);
        const gst = Number(m.gst_sales || 0);
        if (!Number.isFinite(gst) || gst === 0) return;
        if (inFy(dt, fyCurStart)) { ytdSum += gst; ytdDates.push(dt); }
        else if (inFy(dt, fyLastStart)) { lastFySum += gst; }
        else if (inFy(dt, fyBeforeStart)) { beforeSum += gst; beforeDates.push(dt); }
    });
    if (ytdSum === 0 && lastFySum === 0 && beforeSum === 0) return null;
    const shortMonth = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    function rangeLabel(dates) {
        if (!dates.length) return null;
        const s = [...dates].sort((a, b) => a - b);
        const first = `${shortMonth[s[0].getMonth()]}'${String(s[0].getFullYear()).slice(-2)}`;
        const last = `${shortMonth[s[s.length - 1].getMonth()]}'${String(s[s.length - 1].getFullYear()).slice(-2)}`;
        return first === last ? first : `${first}–${last}`;
    }
    const fyLastLabel = `FY${String((fyLastStart + 1) % 100).padStart(2, "0")}`;
    const fyBeforeLabel = `FY${String((fyBeforeStart + 1) % 100).padStart(2, "0")}`;
    return {
        ytd: { amount: ytdSum, range: rangeLabel(ytdDates) },
        lastFy: { amount: lastFySum, label: fyLastLabel },
        before: { amount: beforeSum, label: fyBeforeLabel, range: rangeLabel(beforeDates) }
    };
}

// ---------------------------------------------------------------------------
// Pricing view-model — reads live from caseDetail.scheme (+ term-loan child
// and default_processing_charges FeeMaster). Mirrors _pricing_details in
// credit_brief.py so tile + PDF stay identical.
// ---------------------------------------------------------------------------
function _posNumber(v) {
    if (v === null || v === undefined || v === "") return null;
    const num = Number(v);
    return Number.isFinite(num) && num > 0 ? num : null;
}
function _formatRate(v) {
    const num = Number(v);
    if (!Number.isFinite(num)) return "—";
    return num.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}
function _processingFeePerc(caseDetail) {
    if (!caseDetail) return null;
    const master = (caseDetail.get ? caseDetail.get("default_processing_charges") : caseDetail.default_processing_charges) || null;
    if (!master) return null;
    const configs = (master.get ? master.get("configurations") : master.configurations) || null;
    if (!configs) return null;
    const arr = typeof configs.toJS === "function" ? configs.toJS() : Array.isArray(configs) ? configs : [];
    const percentRow = arr.find(row => {
        if (!row) return false;
        const t = row.fee_calculation_type !== undefined ? row.fee_calculation_type : (row.get && row.get("fee_calculation_type"));
        return Number(t) === 2;
    });
    if (!percentRow) return null;
    const value = percentRow.value !== undefined ? percentRow.value : (percentRow.get && percentRow.get("value"));
    return _posNumber(value);
}
function _emiFrequencyLabel(value) {
    if (value === null || value === undefined || value === "") return null;
    const asNum = Number(value);
    if (asNum === 2) return "Monthly";
    if (asNum === 1) return "Day Set";
    if (asNum === 0) return null;
    if (typeof value === "string") return value;
    return null;
}
export function buildPricing({ caseDetail }) {
    const scheme = caseDetail && (caseDetail.get ? caseDetail.get("scheme") : caseDetail.scheme);
    if (!scheme) return null;
    const schemeGet = key => (scheme.get ? scheme.get(key) : scheme[key]);
    const caseGet = key => caseDetail && (caseDetail.get ? caseDetail.get(key) : caseDetail[key]);

    let roi = _posNumber(schemeGet("roi"));
    if (roi === null) {
        const sched = schemeGet("roi_schedule");
        const first = sched && (sched[0] || (sched.get && sched.get(0)));
        if (first) roi = _posNumber(first.roi || (first.get && first.get("roi")));
    }
    let subvention = null;
    let subventionDays = null;
    const dwd = schemeGet("dwd_sp_default");
    if (dwd) {
        const dwdGet = key => (dwd.get ? dwd.get(key) : dwd[key]);
        subvention = _posNumber(dwdGet("subvention_perc"));
        subventionDays = _posNumber(dwdGet("subvention_days"));
    }
    // Subvention tile shows "days / %" in one card, e.g. "30 days / 2.5%".
    // The days half is prefixed only when present.
    const subventionDisplay =
        subvention !== null
            ? subventionDays !== null
                ? `${Math.round(subventionDays)} days / ${_formatRate(subvention)}%`
                : `${_formatRate(subvention)}%`
            : "—";
    let termLoan = null;
    const tlList = schemeGet("scheme_scheme_term_loan");
    if (tlList) {
        const first = (tlList.get && tlList.get(0)) || (Array.isArray(tlList) ? tlList[0] : null);
        termLoan = first || null;
    }
    const tlGet = key => termLoan && (termLoan.get ? termLoan.get(key) : termLoan[key]);
    const schemeTypeLabel = String(caseGet("scheme_type") || "");
    const isTermLoan = /term\s*loan/i.test(schemeTypeLabel) || Boolean(termLoan);

    let tenureTxt = null;
    if (isTermLoan) {
        const months = _posNumber(tlGet("max_instalment_count"));
        if (months !== null) tenureTxt = `${Math.round(months)} months`;
    }
    if (!tenureTxt) {
        const tenor = _posNumber(schemeGet("tenor"));
        if (tenor !== null && tenor < 9999) tenureTxt = `${Math.round(tenor)} days`;
        else {
            const t = schemeGet("tenure");
            if (t) tenureTxt = String(t);
        }
    }
    // Stop Billing Days — its own pricing tile (e.g. "75 days"), shown when the
    // scheme carries a positive value.
    const stopBillingDays = _posNumber(schemeGet("stop_billing_days"));
    const sbdTxt =
        stopBillingDays !== null ? `${Math.round(stopBillingDays)} days` : null;

    const margin = _posNumber(schemeGet("margin"));
    const securityDeposit = _posNumber(schemeGet("security_deposit"));
    const pfPerc = _processingFeePerc(caseDetail);
    const emiFreq = _emiFrequencyLabel(tlGet("instalment_frequency"));

    const items = [];
    // For non-Term-Loan (Invoice Finance / WCDL) cases, ALWAYS emit the same
    // 4 tiles the credit-brief PDF shows (ROI, Tenure, Subvention %, Margin) —
    // use "—" when a value is missing so the layout stays stable. Term Loan
    // extra tiles are still conditional.
    if (!isTermLoan) {
        items.push({
            label: "ROI",
            value: roi !== null ? `${_formatRate(roi)}%` : "—"
        });
        items.push({ label: "Tenure", value: tenureTxt || "—" });
        items.push({ label: "SBD", value: sbdTxt || "—" });
        items.push({ label: "Subvention %", value: subventionDisplay });
        items.push({
            label: "Margin",
            value: margin !== null ? `${_formatRate(margin)}%` : "—"
        });
        if (securityDeposit !== null) {
            items.push({
                label: "Security Deposit",
                value: `${_formatRate(securityDeposit)}%`
            });
        }
    } else {
        if (roi !== null) items.push({ label: "ROI", value: `${_formatRate(roi)}%` });
        if (tenureTxt) items.push({ label: "Tenure", value: tenureTxt });
        if (sbdTxt) items.push({ label: "SBD", value: sbdTxt });
        if (subvention !== null) items.push({ label: "Subvention %", value: subventionDisplay });
        if (margin !== null) items.push({ label: "Margin", value: `${_formatRate(margin)}%` });
        if (securityDeposit !== null) items.push({ label: "Security Deposit", value: `${_formatRate(securityDeposit)}%` });
        if (pfPerc !== null) items.push({ label: "PF %", value: `${_formatRate(pfPerc)}%` });
        if (emiFreq) items.push({ label: "EMI Frequency", value: emiFreq });
    }
    return items.length ? items : null;
}

export default buildApplicants;
